/**
 * Centralized query keys factory for type-safe query key management.
 * Each entity has its own namespace with standardized query key patterns.
 *
 * Queries are defined by `queryOptions` factories that consume these keys —
 * shared reads in `lib/query-options.ts`, feature reads in
 * `modules/<feature>/query/query-options.ts`, route-scoped reads in the
 * route's `-query/query-options.ts`.
 *
 * @example
 * ```typescript
 * // Basic definition
 * const makeQueryKeys = {
 *   users: {
 *     all: () => ['users'] as const,
 *     index: (filters?: Record<string, unknown>) => ['users', 'index', filters] as const,
 *     show: (id: string) => ['users', id] as const
 *   }
 * } as const
 *
 * // Query — factory built on these keys
 * const userQuery = useQuery(userQueryOptions('123'))
 *
 * // Query with filters
 * const filteredUsers = useQuery(
 *   usersListQueryOptions({ status: 'active', role: 'admin' })
 * )
 *
 * // Mutation — manual mutationFn, body typed from Api*Data['body']
 * const updateUser = useMutation({
 *   mutationFn: (body: ApiUsersUpdateMeData['body']) =>
 *     apiUsersUpdateMe({ body }),
 *   onSuccess: () =>
 *     queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 * })
 *
 * // Invalidate all user queries (array prefix matching)
 * queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 *
 * // Invalidate one filtered list
 * queryClient.invalidateQueries({
 *   queryKey: makeQueryKeys.users.index({ status: 'active' })
 * })
 *
 * // Prefetch / SSR ensure
 * queryClient.ensureQueryData(userQueryOptions('123'))
 * ```
 */
export const makeQueryKeys = {
	auth: {
		all: () => ['auth'] as const,
		session: () => ['auth', 'session'] as const,
		sessionsAll: () => ['auth', 'sessions'] as const,
		sessions: (body: Record<string, unknown>) =>
			['auth', 'sessions', body] as const
	},
	users: {
		all: () => ['users'] as const,
		index: (filters?: Record<string, unknown>) =>
			['users', 'index', filters] as const,
		detail: (userId: string) => ['users', 'detail', userId] as const,
		sessions: (userId: string, body: Record<string, unknown>) =>
			['users', 'detail', userId, 'sessions', body] as const
	},
	admins: {
		all: () => ['admins'] as const,
		me: () => ['admins', 'me'] as const,
		index: (filters?: Record<string, unknown>) =>
			['admins', 'index', filters] as const
	},
	exports: {
		detail: (id: string) => ['exports', 'detail', id] as const
	},
	residences: {
		all: () => ['residences'] as const,
		dsuRate: () => ['residences', 'dsu-rate'] as const,
		index: (filters?: Record<string, unknown>) =>
			['residences', 'index', filters] as const,
		detail: (residenceId: string) =>
			['residences', 'detail', residenceId] as const,
		managers: (residenceId: string) =>
			['residences', 'detail', residenceId, 'managers'] as const,
		mailboxTest: (residenceId: string) =>
			['residences', 'detail', residenceId, 'mailbox-test'] as const
	},
	geo: {
		all: () => ['geo'] as const,
		institutions: () => ['geo', 'institutions'] as const,
		municipalities: (search: string) =>
			['geo', 'municipalities', search] as const
	},
	emailTexts: {
		all: () => ['email-texts'] as const,
		index: () => ['email-texts', 'index'] as const,
		detail: (code: string) => ['email-texts', 'detail', code] as const
	},
	adminHome: {
		all: () => ['admin-home'] as const,
		requests: () => ['admin-home', 'requests'] as const
	},
	managerDashboard: {
		all: () => ['manager-dashboard'] as const,
		detail: (residenceId: string | undefined) =>
			['manager-dashboard', residenceId ?? 'all'] as const
	},
	support: {
		all: () => ['support'] as const,
		search: (query: string) => ['support', 'search', query] as const,
		student: (userId: string) => ['support', 'student', userId] as const
	},
	sendingAnomalies: {
		all: () => ['sending-anomalies'] as const,
		residences: () => ['sending-anomalies', 'residences'] as const,
		index: (body: Record<string, unknown>) =>
			['sending-anomalies', 'index', body] as const,
		detail: (anomalyId: string) =>
			['sending-anomalies', 'detail', anomalyId] as const
	},
	// The address a student's email was refused at never changes, and reading it
	// is an access to log: it has a root of its own so that the commands of the
	// anomaly, which invalidate `sendingAnomalies`, do not read it again.
	sendingRecipients: {
		detail: (anomalyId: string) =>
			['sending-recipients', anomalyId] as const
	},
	monitoring: {
		all: () => ['monitoring'] as const,
		calls: (filters?: Record<string, unknown>) =>
			['monitoring', 'calls', filters] as const
	},
	ranking: {
		all: () => ['ranking'] as const,
		control: (residenceId: string) =>
			['ranking', 'control', residenceId] as const,
		controlPage: (residenceId: string, body: unknown) =>
			['ranking', 'control', residenceId, body] as const,
		recalculation: (residenceId: string) =>
			['ranking', 'recalculation', residenceId] as const,
		provisional: (residenceId: string) =>
			['ranking', 'provisional', residenceId] as const,
		provisionalPage: (residenceId: string, body: unknown) =>
			['ranking', 'provisional', residenceId, body] as const,
		reports: (residenceId: string, state: string, callId?: string) =>
			[
				'ranking',
				'reports',
				residenceId,
				state,
				callId ?? 'current'
			] as const,
		report: (residenceId: string, reportId: string) =>
			['ranking', 'report', residenceId, reportId] as const
	},
	offers: {
		all: () => ['offers'] as const,
		preview: (residenceId: string) =>
			['offers', 'preview', residenceId] as const,
		list: (residenceId: string, body: unknown) =>
			['offers', 'list', residenceId, body] as const,
		pecText: (residenceId: string, offerId: string, sentAt: string) =>
			['offers', 'pec-text', residenceId, offerId, sentAt] as const,
		expiries: (residenceId: string, callId?: string) =>
			['offers', 'expiries', residenceId, callId ?? 'current'] as const
	},
	verifications: {
		all: () => ['verifications'] as const,
		index: (residenceId: string) =>
			['verifications', 'index', residenceId] as const,
		neighbors: (
			residenceId: string,
			verificationId: string,
			filters: unknown
		) =>
			[
				'verifications',
				'index',
				residenceId,
				'neighbors',
				verificationId,
				filters
			] as const,
		detail: (residenceId: string, verificationId: string) =>
			['verifications', 'detail', residenceId, verificationId] as const,
		file: (residenceId: string, fileId: string) =>
			['verifications', 'file', residenceId, fileId] as const
	},
	rooms: {
		all: () => ['rooms'] as const,
		overview: (residenceId: string) =>
			['rooms', 'overview', residenceId] as const,
		overviewPage: (residenceId: string, body: unknown) =>
			['rooms', 'overview', residenceId, body] as const
	},
	dsuQuota: {
		all: () => ['dsu-quota'] as const,
		detail: (residenceId: string) =>
			['dsu-quota', 'detail', residenceId] as const
	},
	flags: {
		all: () => ['flags'] as const,
		index: (residenceId: string, body?: Record<string, unknown>) =>
			['flags', 'index', residenceId, body] as const,
		detail: (residenceId: string, flagId: string) =>
			['flags', 'detail', residenceId, flagId] as const,
		file: (residenceId: string, fileId: string) =>
			['flags', 'file', residenceId, fileId] as const
	},
	applications: {
		all: () => ['applications'] as const,
		index: (residenceId: string, body?: Record<string, unknown>) =>
			['applications', 'index', residenceId, body] as const,
		detail: (residenceId: string, submissionId: string) =>
			['applications', 'detail', residenceId, submissionId] as const,
		samePerson: (residenceId: string, taxCode: string) =>
			['applications', 'same-person', residenceId, taxCode] as const,
		status: (residenceId: string, submissionId: string) =>
			[
				'applications',
				'detail',
				residenceId,
				submissionId,
				'status'
			] as const
	},
	call: {
		all: () => ['call'] as const,
		list: () => ['call', 'list'] as const,
		home: (residenceId: string, callId?: string) =>
			['call', 'home', residenceId, callId ?? 'current'] as const,
		managed: (residenceId: string, callId?: string) =>
			['call', 'managed', residenceId, callId ?? 'current'] as const,
		mailboxTest: (residenceId: string, callId?: string) =>
			[
				'call',
				'managed',
				residenceId,
				callId ?? 'current',
				'mailbox-test'
			] as const
	}
} as const
